import { toast } from "sonner";

/**
 * Standard toast after a bulk-delete run (possibly across multiple batches).
 *
 * Accepts either a per-batch result (legacy, single call) or the aggregate
 * returned by runChunkedBulkDelete. Renders exactly one toast:
 *   • fully done            → success ("N deleted")
 *   • partial per-record    → warning ("N deleted · M failed (reason)")
 *   • stopped mid-way       → error ("Stopped after batch X — N deleted, S remaining")
 *   • nothing succeeded     → error
 */
export function reportBulkDeleteResult(result, { labelSingular, labelPlural }) {
    const deleted = result?.deleted || 0;
    const failed  = result?.failed  || 0;
    const skipped = result?.skipped || 0;
    const stopped = !!result?.stopped;
    const nounDel = deleted === 1 ? labelSingular : labelPlural;
    const nounFail = failed === 1 ? labelSingular : labelPlural;

    if (stopped) {
        const batchNo = result?.failedBatch?.number;
        const suffix = skipped > 0 ? `, ${skipped} remaining` : "";
        toast.error(
            `Stopped after batch ${batchNo || "?"} — ${deleted} ${nounDel} deleted${suffix}`,
        );
        return;
    }

    if (deleted && !failed) {
        toast.success(`${deleted} ${nounDel} deleted`);
        return;
    }
    if (deleted && failed) {
        const firstReason = result?.failures?.[0]?.reason || "unknown";
        toast.warning(
            `${deleted} ${nounDel} deleted · ${failed} ${nounFail} failed (${humanReason(firstReason)})`,
        );
        return;
    }
    if (!deleted && failed) {
        const firstReason = result?.failures?.[0]?.reason || "unknown";
        toast.error(`${failed} ${nounFail} could not be deleted (${humanReason(firstReason)})`);
        return;
    }
    toast.info("Nothing to delete");
}

function humanReason(raw) {
    return {
        not_found: "not found",
        already_deleted: "already deleted",
        cannot_delete_self: "cannot delete yourself",
        last_admin_protected: "last active administrator",
        delete_failed: "delete failed",
    }[raw] || raw;
}
